import {
  bufferToHexString,
  hexStringToBuffer as hexStringToBufferCore,
} from "@iotize/common/byte-converter";
import { safeEnumValue } from "@iotize/common/utility";
import { ConnectionState } from "@iotize/tap/protocol/api";
import { Observable, Subject, defer } from "rxjs";
import { switchMap } from "rxjs/operators";
import { map } from "rxjs/operators";
import { CordovaInterface } from "./cordova-interface";
import {
  CordovaBLEScanResult,
  RequestDeviceOptions,
  ServiceList,
} from "./definitions";
import { CordovaBLEError } from "./cordova-ble-error";

function hexStringToBuffer(str: string): Uint8Array {
  try {
    return hexStringToBufferCore(str);
  } catch (err) {
    throw CordovaBLEError.invalidNativeCallResult(str, err as Error);
  }
}

declare var cordova: CordovaInterface;

export class IoTizeBleCordovaPlugin {
  checkAvailable(): Promise<boolean> {
    return this.execSingleResult<boolean>("checkAvailable", []);
  }

  requestEnableBle(): Promise<"OK"> {
    return this.execSingleResult<"OK">("enable", []);
  }

  startScan(
    requestDeviceOptions: RequestDeviceOptions | undefined
  ): Observable<CordovaBLEScanResult | "Ok"> {
    return this.execMultipleResult<CordovaBLEScanResult | "Ok">("startScan", [
      JSON.stringify(requestDeviceOptions),
    ]);
  }

  stopScan() {
    return this.execSingleResult<"OK">("stopScan", []);
  }

  connect(
    deviceId: string,
    enableBleIfNot: boolean = true
  ): Observable<ConnectionState> {
    return this.askBleEnable(enableBleIfNot).pipe(
      switchMap(() => {
        return this.execMultipleResult<string>("connect", [deviceId]).pipe(
          map((state) => {
            if (!(state in ConnectionState)) {
              console.warn(
                `Plugin native code returned an invalid connection state: "${state}".`
              );
            }
            return safeEnumValue(ConnectionState, state);
          })
        );
      })
    );
  }

  askBleEnable(askEnable: boolean): Observable<void> {
    return defer(async () => {
      if (askEnable) {
        if (!(await this.checkAvailable())) {
          await this.requestEnableBle();
        }
      }
    });
  }

  requestMTU(deviceId: string, mtu: number) {
    return this.execSingleResult<"OK">("requestMTU", [deviceId, mtu]);
  }

  connectAndDiscoverTapServices(
    deviceId: string,
    enableBleIfNot: boolean = true
  ): Observable<ConnectionState> {
    return this.askBleEnable(enableBleIfNot).pipe(
      switchMap(() => {
        return this.execMultipleResult<string>(
          "connectAndDiscoverTapServices",
          [deviceId]
        ).pipe(
          map((state) => {
            if (!(state in ConnectionState)) {
              console.warn(
                `Plugin native code returned an invalid connection state: "${state}".`
              );
            }
            return safeEnumValue(ConnectionState, state);
          })
        );
      })
    );
  }

  disConnect(deviceId: string) {
    return this.execSingleResult<"OK">("disConnect", [deviceId]);
  }

  close(deviceId: string) {
    return this.execSingleResult<"OK">("close", [deviceId]);
  }

  isConnected(deviceId: string) {
    return this.execSingleResult<boolean>("isConnected", [deviceId]);
  }

  async send(deviceId: string, data: Uint8Array) {
    const hexString = await this.execSingleResult<string>("sendRequest", [
      deviceId,
      bufferToHexString(data),
    ]);
    return hexStringToBuffer(hexString);
  }

  getLastError() {
    return this.execSingleResult<string>("getLastError", []);
  }

  characteristicStartNotification(
    deviceId: string,
    serviceId: string,
    characId: string
  ) {
    return this.execSingleResult<"OK">("characteristicStartNotification", [
      deviceId,
      serviceId,
      characId,
    ]);
  }

  characteristicChanged(
    deviceId: string,
    serviceId: string,
    characId: string
  ): Observable<Uint8Array> {
    return this.execMultipleResult<string>("characteristicChanged", [
      deviceId,
      serviceId,
      characId,
    ]).pipe(
      map((hexString) => {
        return hexStringToBuffer(hexString);
      })
    );
  }

  characteristicStopNotification(
    deviceId: string,
    serviceId: string,
    characId: string
  ) {
    return this.execSingleResult<"OK">("characteristicStopNotification", [
      deviceId,
      serviceId,
      characId,
    ]);
  }

  async characteristicReadValue(
    deviceId: string,
    serviceId: string,
    characId: string
  ): Promise<Uint8Array> {
    const hexString = await this.execSingleResult<string>(
      "characteristicRead",
      [deviceId, serviceId, characId]
    );
    return hexStringToBuffer(hexString);
  }

  characteristicWrite(
    deviceId: string,
    serviceId: string,
    characId: string,
    data: Uint8Array
  ) {
    return this.execSingleResult<"OK">("characteristicWrite", [
      deviceId,
      serviceId,
      characId,
      bufferToHexString(data),
    ]);
  }

  characteristicWriteWithoutResponse(
    deviceId: string,
    serviceId: string,
    characId: string,
    data: Uint8Array
  ) {
    return this.execSingleResult<"OK">("characteristicWriteWithoutResponse", [
      deviceId,
      serviceId,
      characId,
      bufferToHexString(data),
    ]);
  }

  async discoverServices(deviceId: string): Promise<ServiceList> {
    const services = await this.execSingleResult<ServiceList>(
      "discoverServices",
      [deviceId]
    );
    return services;
  }

  private execSingleResult<T extends string | void | boolean | Object>(
    methodName: string,
    args: any[]
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      cordova.exec(
        resolve,
        (errObject: { code?: string; message?: string }) => {
          reject(objectErrorToError(errObject));
        },
        "BLECom",
        methodName,
        args
      );
    });
  }

  private execMultipleResult<T>(
    methodName: string,
    args: any[]
  ): Observable<T> {
    const subject = new Subject<T>();
    cordova.exec<T>(
      (data) => {
        subject.next(data);
      },
      (err) => {
        subject.error(objectErrorToError(err));
      },
      "BLECom",
      methodName,
      args
    );
    return subject.asObservable();
  }
}

function objectErrorToError(errObject: { code?: string; message?: string }) {
  if (typeof errObject !== "object" || !errObject.code || !errObject.message) {
    return CordovaBLEError.invalidErrorResult(errObject);
  }
  if (!CordovaBLEError.isValidErrorCode(errObject.code)) {
    return CordovaBLEError.invalidErrorCode(errObject);
  }
  return new CordovaBLEError(
    errObject.message,
    CordovaBLEError.Code[errObject.code]
  );
}
